import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import { Button, Card } from "../../../ui";

export const GOAL_INPUT_ID = "home-goal";

interface GoalBoxProps {
  goal: string;
  onGoalChange(text: string): void;
  suggestions: readonly string[];
}

/**
 * The goal box of the home (workflow 01): the engineer says in their own words what they want to do. The
 * suggestions fill the box and change nothing until the engineer presses the button. The session starts
 * with the goal; the item can be named now or in the session.
 */
export function GoalBox({ goal, onGoalChange, suggestions }: GoalBoxProps) {
  const navigate = useNavigate();
  const start = useApiCommand("start_session");
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const text = goal.trim();
    if (!text) {
      setProblem("Write what you want to do first.");
      return;
    }
    setProblem(null);
    try {
      const reply = await start.mutateAsync({ goal: text });
      void navigate(`/sessions/${encodeURIComponent(reply.data.session.id)}`);
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Card aria-labelledby="goal-title" className="nv-goal">
      <h2 id="goal-title">What do you want to work on?</h2>
      <form onSubmit={(event) => void submit(event)}>
        <label htmlFor={GOAL_INPUT_ID} className="field__label">
          Your goal, in your own words
        </label>
        <p className="field__hint muted">You can name the item now or in the session. Pick an item in the tree below if you prefer.</p>
        <div className="nv-goal__row">
          <input
            id={GOAL_INPUT_ID}
            className="field__input"
            value={goal}
            onChange={(event) => onGoalChange(event.target.value)}
            autoComplete="off"
            aria-invalid={problem ? true : undefined}
            aria-describedby={problem ? "goal-problem" : undefined}
          />
          <Button type="submit" variant="primary" busy={start.isPending}>
            Start a session
          </Button>
        </div>
        {problem && (
          <p id="goal-problem" role="alert" className="field__error">
            {problem}{" "}
            <Link to="/sessions/new">Pick an item first</Link>
          </p>
        )}
      </form>
      {suggestions.length > 0 && (
        <div className="nv-goal__suggestions" role="group" aria-label="Suggestions">
          <span className="muted">Or start from:</span>
          <ul className="row">
            {suggestions.map((text) => (
              <li key={text}>
                <button type="button" className="nv-chip" onClick={() => onGoalChange(text)}>
                  {text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
